/**
 * QuotationsPanel - Painel de Cotações Simplificado
 * 
 * VERSÃO SIMPLIFICADA - Foco no que importa
 * 
 * Baseado no tripé: 
 * ⚖️ JUSTIÇA - Dados verificáveis com cálculos corretos
 * 🔒 SEGURANÇA - Fontes oficiais (IEA, APABOR, CONAB)
 * ✨ SIMPLICIDADE - Informação clara e organizada
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useTranslation } from '../../translations';

const QuotationsPanel = () => {
  const { t } = useTranslation();
  const [quotations, setQuotations] = useState(null);
  const [historical, setHistorical] = useState([]);
  const [marginAnalysis, setMarginAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showContext, setShowContext] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchMarketData();
  }, []);

  const fetchMarketData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [quotationsRes, historicalRes, marginRes] = await Promise.all([
        fetch(`${API_URL}/api/market/quotations`),
        fetch(`${API_URL}/api/market/historical`),
        fetch(`${API_URL}/api/market/margin-analysis`)
      ]);

      if (!quotationsRes.ok || !historicalRes.ok || !marginRes.ok) {
        throw new Error('Erro ao carregar dados do mercado');
      }

      const quotationsData = await quotationsRes.json();
      const historicalData = await historicalRes.json();
      const marginData = await marginRes.json();

      const quotationsByType = {};
      quotationsData.quotations.forEach(q => {
        quotationsByType[q.type] = q;
      });

      setQuotations(quotationsByType);
      setHistorical(historicalData.historical || []);
      setMarginAnalysis(marginData);

    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError('Não foi possível carregar os dados do mercado. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('qpLoadingQuotes')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-red-300 bg-red-50">
        <CardContent className="pt-6">
          <div className="text-center">
            <span className="text-4xl">⚠️</span>
            <p className="text-red-800 font-medium mt-2">{error}</p>
            <button 
              onClick={fetchMarketData}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Tentar Novamente
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const coagulo = quotations?.coagulo || {};
  const geb10Apabor = quotations?.geb10_apabor || {};
  const internacional = quotations?.internacional || {};
  const cambio = quotations?.cambio || {};
  const custoProducao = quotations?.custo_producao_cot || quotations?.custo_producao || {};
  const geb10Estimado = quotations?.geb10_estimado || null;

  const geb10AgeDays = (() => {
    if (!geb10Apabor.updated_at) return null;
    const d = new Date(geb10Apabor.updated_at);
    if (isNaN(d.getTime())) return null;
    return Math.floor((Date.now() - d.getTime()) / 86400000);
  })();
  const officialStale = geb10AgeDays === null || geb10AgeDays > 75;
  const useEstimado = !!(officialStale && geb10Estimado?.value);
  const geb10Best = useEstimado ? geb10Estimado : geb10Apabor;

  // Cálculo CORRETO da taxa de participação: Coágulo / (GEB-10 × DRC)
  const drcPadrao = 0.53;
  const geb10Ajustado = (geb10Best.value || 0) * drcPadrao;
  const taxaParticipacao = geb10Ajustado > 0 ? ((coagulo.value || 0) / geb10Ajustado) * 100 : 0;

  return (
    <div className="space-y-4">
      {/* Header com Disclaimer */}
      <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-green-50">
        <CardContent className="py-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span className="text-lg">📊</span>
              <p className="text-sm text-gray-700">
                {t('officialSourcesDisclaimer')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setShowHelp(!showHelp)}
                className="text-sm px-3 py-1 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 flex items-center gap-1"
                title={t('howDataWorks') || 'Como funcionam os dados'}
              >
                💡 {t('understand') || 'Entenda'}
              </button>
              <button 
                onClick={fetchMarketData}
                className="text-sm px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 flex items-center gap-1"
              >
                🔄 {t('update') || 'Atualizar'}
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tooltip de Ajuda - Como funcionam os dados */}
      {showHelp && (
        <Card className="border-2 border-amber-300 bg-amber-50">
          <CardContent className="py-4">
            <div className="flex justify-between items-start mb-3">
              <h4 className="font-bold text-amber-800 flex items-center gap-2">
                💡 Como Funcionam os Dados desta Página
              </h4>
              <button onClick={() => setShowHelp(false)} className="text-amber-600 hover:text-amber-800 text-lg">✕</button>
            </div>
            
            <div className="space-y-4 text-sm">
              {/* Dados principais */}
              <div className="bg-white p-3 rounded-lg border">
                <p className="font-bold text-gray-800 mb-2">📊 Os 2 dados principais:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-start gap-2">
                    <span className="text-green-600">🏭</span>
                    <div>
                      <p className="font-medium text-green-800">GEB-10 APABOR</p>
                      <p className="text-xs text-gray-600">{t('qpNegotiationRef')}</p>
                      <p className="text-xs text-gray-500">{t('qpUpdate')}: <strong>{t('qpBimonthlyUpdate')}</strong> (6x/ano)</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-green-600">🌿</span>
                    <div>
                      <p className="font-medium text-green-800">{t('qpCoaguloRealPrice')}</p>
                      <p className="text-xs text-gray-600">{t('qpRealPriceDesc')}</p>
                      <p className="text-xs text-gray-500">{t('qpUpdate')}: <strong>{t('qpMonthlyUpdate')}</strong> {t('qpDays15to20')}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Taxa de Participação */}
              <div className="bg-white p-3 rounded-lg border">
                <p className="font-bold text-gray-800 mb-2">📐 Taxa de Participação Implícita:</p>
                <div className="bg-gray-50 p-2 rounded font-mono text-xs">
                  Taxa = Coágulo ÷ (GEB-10 × DRC 53%)
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  Mostra qual percentual do GEB-10 está sendo pago ao produtor. 
                  Referência mínima CNA: <strong>52%</strong> | Referência IEA: <strong>80%</strong>
                </p>
              </div>

              {/* Dados de contexto */}
              <div className="bg-white p-3 rounded-lg border">
                <p className="font-bold text-gray-800 mb-2">📈 Dados de Contexto (expandível):</p>
                <div className="text-xs text-gray-600 space-y-1">
                  <p>• <strong>{t('qpInternationalSGX')}</strong> - {t('qpSGXDesc')}</p>
                  <p>• <strong>{t('qpDollarBCB')}</strong> - {t('qpDollarDesc')}</p>
                  <p>• <strong>{t('qpProdCostCONAB')}</strong> - {t('qpProdCostDesc')}</p>
                </div>
              </div>

              {/* Como atualizar */}
              <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                <p className="font-bold text-green-800 mb-2">🔄 Como os dados são atualizados:</p>
                <div className="text-xs text-green-700 space-y-1">
                  <p>• <strong>Automático:</strong> Dólar (1 clique na aba "📡 Status")</p>
                  <p>• <strong>Manual:</strong> GEB-10 e Coágulo (via "⚙️ Atualizar Dados" na aba Status)</p>
                  <p>• Os dados vêm de fontes oficiais: IEA, APABOR, CONAB, BCB</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ============================================ */}
      {/* SEÇÃO PRINCIPAL: O QUE IMPORTA */}
      {/* ============================================ */}
      <Card className="border-2 border-green-300 shadow-lg">
        <CardHeader className="pb-2 bg-gradient-to-r from-green-50 to-blue-50">
          <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
            💰 {t('marketSummary')}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {/* Cards principais lado a lado */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* GEB-10 - Referência (oficial ou estimado) */}
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-200" data-testid="geb10-card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-700">🏭 {useEstimado ? 'GEB-10 Estimado' : 'GEB-10 APABOR'}</span>
                <span className={`text-xs px-2 py-0.5 rounded ${useEstimado ? 'bg-purple-200 text-purple-800' : 'bg-blue-200 text-blue-800'}`} data-testid="geb10-badge">
                  {useEstimado ? '🛰️ ESTIMADO' : (t('reference') || 'REFERÊNCIA')}
                </span>
              </div>
              <p className="text-3xl font-bold text-blue-800">
                R$ {(geb10Best.value || 0).toFixed(2).replace('.', ',')}
                <span className="text-sm font-normal text-blue-600">/kg</span>
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {useEstimado
                  ? `${geb10Estimado.reference_date || ''} • TSR-20 × PTAX (diário)`
                  : `${geb10Apabor.reference_date || 'N/A'} • ${t('bimonthly') || 'Bimestral'}`}
              </p>
              {useEstimado ? (
                <p className="text-[11px] text-gray-400 mt-1">
                  Último oficial APABOR: R$ {(geb10Apabor.value || 0).toFixed(2).replace('.', ',')} ({geb10Apabor.reference_date}) — desatualizado
                </p>
              ) : (
                <p className="text-[11px] text-gray-400 mt-1" data-testid="geb10-explain-note">
                  Preço oficial do fechamento bimestral (usado nos contratos).
                  {geb10Estimado?.value ? ` Indicador diário (TSR-20 × PTAX): R$ ${geb10Estimado.value.toFixed(2).replace('.', ',')} — oscila com a bolsa e não é o preço praticado.` : ' O indicador diário do site da APABOR oscila com a bolsa e não é o preço praticado.'}
                </p>
              )}
            </div>

            {/* Coágulo - Preço Real */}
            <div className="bg-green-50 rounded-xl p-4 border border-green-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-green-700">🌿 {t('coagulum') || 'Coágulo'} ({t('producer') || 'Produtor'})</span>
                <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded">{t('realPrice') || 'PREÇO REAL'}</span>
              </div>
              <p className="text-3xl font-bold text-green-800">
                R$ {(coagulo.value || 0).toFixed(2).replace('.', ',')}
                <span className="text-sm font-normal text-green-600">/kg</span>
              </p>
              <p className="text-xs text-gray-500 mt-1">
                DRC 53% • {coagulo.reference_date || 'N/A'}
              </p>
            </div>
          </div>

          {/* Taxa de Participação - CORRIGIDA */}
          <div className="bg-gray-50 rounded-xl p-4 border">
            <div className="flex items-center justify-between mb-3">
              <div>
                <span className="text-sm font-medium text-gray-700">📊 {t('impliedParticipationRate') || 'Taxa de Participação Implícita'}</span>
                <p className="text-xs text-gray-500">Coágulo ÷ (GEB-10 × DRC 53%)</p>
              </div>
              <div className="text-right">
                <span className={`text-2xl font-bold ${taxaParticipacao >= 52 ? 'text-green-700' : 'text-amber-600'}`}>
                  {taxaParticipacao.toFixed(1).replace('.', ',')}%
                </span>
              </div>
            </div>
            
            {/* Barra de progresso */}
            <div className="relative h-4 bg-gray-200 rounded-full overflow-hidden">
              <div className="absolute left-0 top-0 h-full bg-red-200" style={{ width: '52%' }}></div>
              <div className="absolute left-[52%] top-0 h-full bg-yellow-200" style={{ width: '28%' }}></div>
              <div className="absolute left-[80%] top-0 h-full bg-green-200" style={{ width: '20%' }}></div>
              <div 
                className={`absolute top-0 h-full ${taxaParticipacao >= 80 ? 'bg-green-500' : taxaParticipacao >= 52 ? 'bg-yellow-500' : 'bg-red-500'}`}
                style={{ width: `${Math.min(taxaParticipacao, 100)}%` }}
              ></div>
            </div>
            
            {/* Legenda da barra */}
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>0%</span>
              <span className="text-red-600">52% mín (CNA)</span>
              <span className="text-green-600">80% ref (IEA)</span>
              <span>100%</span>
            </div>

            {/* Explicação do cálculo */}
            <div className="mt-3 p-2 bg-white rounded border text-xs text-gray-600">
              <strong>Cálculo:</strong> R$ {(coagulo.value || 0).toFixed(2).replace('.', ',')} ÷ (R$ {(geb10Best.value || 0).toFixed(2).replace('.', ',')} × 53%) = R$ {(coagulo.value || 0).toFixed(2).replace('.', ',')} ÷ R$ {geb10Ajustado.toFixed(2).replace('.', ',')} = <strong>{taxaParticipacao.toFixed(1).replace('.', ',')}%</strong>
              {useEstimado && <span className="text-purple-600"> • base: GEB-10 estimado 🛰️</span>}
            </div>
          </div>

          {/* Análise de Margem Simplificada */}
          {marginAnalysis && (
            <div className={`mt-4 rounded-xl p-4 border ${marginAnalysis.is_profitable ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-gray-700">💵 Margem sobre Custo de Produção</span>
                  <p className="text-xs text-gray-500">Coágulo R$ {marginAnalysis.current_coagulo_price?.toFixed(2).replace('.', ',')} − Custo R$ {marginAnalysis.production_cost?.toFixed(2).replace('.', ',')}</p>
                </div>
                <div className="text-right">
                  <span className={`text-2xl font-bold ${marginAnalysis.is_profitable ? 'text-green-700' : 'text-red-700'}`}>
                    {marginAnalysis.is_profitable ? '+' : ''}R$ {marginAnalysis.margin_per_kg?.toFixed(2).replace('.', ',')}
                  </span>
                  <p className={`text-xs ${marginAnalysis.is_profitable ? 'text-green-600' : 'text-red-600'}`}>
                    {marginAnalysis.is_profitable ? '✅ Lucro' : '⚠️ Prejuízo'} ({marginAnalysis.margin_percentage?.toFixed(1).replace('.', ',')}%)
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ============================================ */}
      {/* SEÇÃO DE CONTEXTO (Expandível) */}
      {/* ============================================ */}
      <Card className="border border-gray-200">
        <CardHeader 
          className="pb-2 cursor-pointer hover:bg-gray-50"
          onClick={() => setShowContext(!showContext)}
        >
          <CardTitle className="text-base text-gray-700 flex items-center justify-between">
            <span className="flex items-center gap-2">
              📈 Dados de Contexto
              <span className="text-xs font-normal text-gray-500">(Internacional, Câmbio, Custo)</span>
            </span>
            <span className="text-gray-400">{showContext ? '▼' : '▶'}</span>
          </CardTitle>
        </CardHeader>
        
        {showContext && (
          <CardContent className="pt-2">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Internacional */}
              <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm">🌍</span>
                  <span className="text-xs font-medium text-purple-700">Internacional (SGX)</span>
                </div>
                <p className="text-xl font-bold text-purple-800">
                  US$ {(internacional.value || 0).toFixed(2).replace('.', ',')}
                </p>
                <p className="text-xs text-gray-500">
                  = R$ {((internacional.value || 0) * (cambio.value || 1)).toFixed(2).replace('.', ',')}/kg
                </p>
              </div>

              {/* Câmbio */}
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm">💱</span>
                  <span className="text-xs font-medium text-blue-700">Dólar (BCB)</span>
                </div>
                <p className="text-xl font-bold text-blue-800">
                  R$ {(cambio.value || 0).toFixed(2).replace('.', ',')}
                </p>
                <p className="text-xs text-gray-500">
                  {cambio.reference_date || 'Atualização automática'}
                </p>
              </div>

              {/* Custo de Produção */}
              <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm">📦</span>
                  <span className="text-xs font-medium text-orange-700">Custo Produção (COT)</span>
                </div>
                <p className="text-xl font-bold text-orange-800">
                  R$ {(custoProducao.value || 6.07).toFixed(2).replace('.', ',')}
                </p>
                <p className="text-xs text-gray-500">
                  CONAB • {custoProducao.reference_date || '—'}
                </p>
              </div>
            </div>

            <div className="mt-3 p-2 bg-gray-100 rounded text-xs text-gray-600">
              <strong>ℹ️ Como esses dados se relacionam:</strong> O preço internacional + câmbio + custos de importação formam o GEB-10 (referência internalizada). O custo de produção é usado para calcular a margem do produtor.
            </div>
          </CardContent>
        )}
      </Card>

      {/* ============================================ */}
      {/* GRÁFICO HISTÓRICO (Expandível) */}
      {/* ============================================ */}
      {historical.length > 0 && (
        <Card className="border border-gray-200">
          <CardHeader 
            className="pb-2 cursor-pointer hover:bg-gray-50"
            onClick={() => setShowHistory(!showHistory)}
          >
            <CardTitle className="text-base text-gray-700 flex items-center justify-between">
              <span className="flex items-center gap-2">
                📉 Histórico de Preços (12 meses)
              </span>
              <span className="text-gray-400">{showHistory ? '▼' : '▶'}</span>
            </CardTitle>
          </CardHeader>
          
          {showHistory && (
            <CardContent className="pt-2">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={historical}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip 
                      formatter={(value, name) => [
                        `R$ ${value.toFixed(2).replace('.', ',')}`, 
                        name === 'coagulo' ? 'Coágulo' : 'GEB-10'
                      ]}
                    />
                    <Legend 
                      formatter={(value) => value === 'coagulo' ? 'Coágulo (R$/kg)' : 'GEB-10 (R$/kg)'}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="coagulo" 
                      stroke="#22c55e" 
                      strokeWidth={2}
                      dot={{ fill: '#22c55e', r: 3 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="geb10" 
                      stroke="#3b82f6" 
                      strokeWidth={2}
                      dot={{ fill: '#3b82f6', r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className="text-xs text-gray-500 mt-2 text-center">
                Dados históricos baseados em tendências reais. Consulte fontes oficiais para dados precisos.
              </p>
            </CardContent>
          )}
        </Card>
      )}

      {/* Footer */}
      <div className="text-center text-xs text-gray-500 py-2">
        <p>
          ⚖️ TRANSPARÊNCIA: Cálculos verificáveis • 
          🔒 SEGURANÇA: Fontes oficiais • 
          ✨ SIMPLICIDADE: Informação clara
        </p>
      </div>
    </div>
  );
};

export default QuotationsPanel;
